import makeStyles from '@material-ui/core/styles/makeStyles';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { color } from '../../app/theme';
import { PlatformType } from '../../app/types';
import IconX from '../../assets/images/Icons/IconX';
import ImgEmptyTemplate from '../../assets/images/ImgEmptyTemplate';
import ImgEmptySavedreplySearch from '../../assets/images/svg/img-empty-savedreply-search.svg';
import Circular from '../shared/Circular';
import Dropdown from '../shared/Dropdown';
import TextFieldSearch from '../shared/TextFieldSearch';
import DefaultTemplateItem from './DefaultTemplateItem';

const useStyles = makeStyles(
  () => ({
    root: {
      width: 368,
      height: 'calc(100vh - 114px)',
      backgroundColor: color.$22,
      boxShadow: '0 4px 16px 0 rgba(0, 0, 0, 0.08)',
    },
    header: {
      position: 'relative',
      height: 56,
      display: 'flex',
      alignItems: 'center',
      fontSize: 17,
      lineHeight: 1,
      color: color.$13,
      backgroundColor: color.$7,
      padding: '0 16px',
      margin: '0 0 16px',
    },
    'close-icon': {
      cursor: 'pointer',
      position: 'absolute',
      top: '50%',
      right: 16,
      transform: 'translateY(-50%)',
      '&:hover': {
        opacity: 0.6,
      },
      '& > svg': {
        display: 'block',
      },
    },
    content: {
      height: 'calc(100% - 128px)',
      overflowY: 'auto',
    },
    operationBlock: {
      display: 'flex',
      padding: '0 16px',
      margin: '0 0 12px',
    },
    search: {
      flex: '1 1 auto',
      width: '50%',
      margin: '0 4px 0 0',
    },
    dropdown: {
      flex: '1 1 auto',
      width: '50%',
      margin: '0 0 0 4px',
      maxWidth: 150,
      '& > ul': {
        left: '-4px',
      },
    },
    empty: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column',
      height: '100%',
      fontSize: 13,
      lineHeight: 1,
      color: color.$12,
      '& > img': {
        display: 'block',
        marginBottom: 8,
      },
    },
  }),
  {
    name: 'DefaultTemplate',
  },
);

function DefaultTemplate({
  title,
  list,
  onEnter,
  onChange,
  onChangeType,
  templateType,
  onClose,
  onSelectDefaultTemplate,
  onEditDefaultTemplate,
  onDeleteDefaultTemplate,
  rootClass,
  contentClass,
  localKeyword,
  platform,
  isLoading,
  isEdit,
  isSend,
  isCopy,
  isWhatsappTemplate,
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const itemComponents = list
    .filter(data => data.templateType !== 'list')
    .map((data, index) => (
      <DefaultTemplateItem
        key={`DefaultTemplateItem-${index}`}
        data={data}
        isEdit={isEdit}
        isSend={isSend}
        isCopy={isCopy}
        isWhatsappTemplate={isWhatsappTemplate}
        onSelectDefaultTemplate={() => onSelectDefaultTemplate(data)}
        onEditDefaultTemplate={() => onEditDefaultTemplate(data)}
        onDeleteDefaultTemplate={() => onDeleteDefaultTemplate(data)}
      />
    ));

  const menuItems = () => {
    let res = [
      { text: t('types.message-template-type.all'), value: 'all' },
      { text: t('types.message-template-type.card'), value: 'card' },
      {
        text: t('types.message-template-type.confirm'),
        value: 'confirm',
      },
    ];
    if (platform === PlatformType.LINE) {
      res.push({
        text: t('types.message-template-type.image'),
        value: 'image',
      });
    }
    if (platform === PlatformType.WHATSAPP && !isWhatsappTemplate) {
      res.push({
        text: t('types.message-template-type.waList'),
        value: 'waList',
      });
    }
    if (isWhatsappTemplate) {
      res.splice(1, 0, {
        text: t('types.message-template-type.text/plain'),
        value: 'text',
      });
      res = res.filter(d => d.value !== 'image');
      res.splice(2, 0, {
        text: t('types.message-template-type.application/x-file'),
        value: 'file',
      });
    }

    return res;
  };

  return (
    <div className={rootClass || classes.root}>
      <div className={classes.header}>
        {title || t('default-template')}
        <div className={classes['close-icon']} onClick={onClose}>
          <IconX id="close-default-template-dialog" />
        </div>
      </div>
      <div className={classes.operationBlock}>
        <TextFieldSearch
          className={classes.search}
          onEnter={onEnter}
          onChange={onChange}
          placeholder={t('search-default-template-placeholder')}
        />
        <Dropdown
          className={classes.dropdown}
          menuItems={menuItems()}
          value={templateType}
          onChange={onChangeType}
        />
      </div>
      <div
        id="default-template-content"
        className={contentClass || classes.content}
      >
        {isLoading ? (
          <div className="loading">
            <Circular topic="grey" variant="indeterminate" size={20} />
          </div>
        ) : (
          <>
            {itemComponents}
            {list.length === 0 &&
              (localKeyword === '' ? (
                <ImgEmptyTemplate />
              ) : (
                <div className={classes.empty}>
                  <img src={ImgEmptySavedreplySearch} alt="empty" />
                  {t('default-template-not-found')}
                </div>
              ))}
          </>
        )}
      </div>
    </div>
  );
}

DefaultTemplate.defaultProps = {
  list: [],
  rootClass: '',
  contentClass: '',
  localKeyword: '',
  platform: PlatformType.LINE,
  isLoading: false,
};

DefaultTemplate.propTypes = {
  list: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
  rootClass: PropTypes.string,
  contentClass: PropTypes.string,
  localKeyword: PropTypes.string,
  platform: PropTypes.string,
  isLoading: PropTypes.bool,
};

export default DefaultTemplate;
